/**
 * Single source of truth for shortcut-backed actions.
 * Used by the corner nudge, product tour, and keyboard-learning store.
 *
 * Keep this file in sync when adding a new shortcut:
 *  1. Add a ShortcutId + definition here
 *  2. Call usedViaKeyboard(id) in the keyboard path
 *  3. Call usedViaPointer(id) in the matching click/drag/menu path
 */

export type ShortcutId =
  | 'add-service'
  | 'add-database'
  | 'add-api'
  | 'add-person'
  | 'add-queue'
  | 'add-external'
  | 'add-system'
  | 'add-decision'
  | 'add-diamond'
  | 'add-group-node'
  | 'add-box'
  | 'add-note'
  | 'add-text'
  | 'add-participant'
  | 'group-nodes'
  | 'enter-group'
  | 'sidebar-palette'
  | 'sidebar-info'
  | 'sidebar-pages'
  | 'add-socket-in'
  | 'add-socket-out'
  | 'rotate-direction'
  | 'change-color'
  | 'add-layer'
  | 'dim-layer'
  | 'hide-layer'
  | 'draw-frame'
  | 'draw-box'
  | 'toggle-animation'
  | 'distraction-free'
  | 'command-palette'
  | 'ai-chat'
  | 'undo'
  | 'redo'
  | 'delete-selection'
  | 'page-back'
  | 'page-forward'
  | 'rename'
  | 'copy'
  | 'cut'
  | 'paste'
  | 'info-panel'
  | 'inspect'
  | 'select-all'
  | 'resize-drawing'
  | 'resize-shape';

export interface ShortcutDef {
  id: ShortcutId;
  /** Chips shown in the nudge. Chord keys are pressed in sequence, not together. */
  keys: string[];
  /** True = press keys one after another (a then s). False = hold modifiers. */
  chord: boolean;
  /** Short verb phrase, e.g. "Add a Service". */
  label: string;
  /** One-line nudge copy. */
  hint: string;
  /** Independent hold-modifiers, not a simultaneous combo. */
  hold?: boolean;
}

export const SHORTCUTS: Record<ShortcutId, ShortcutDef> = {
  'add-service': { id: 'add-service', keys: ['a', 's'], chord: true, label: 'Add a Service', hint: 'Drop a Service node from the keyboard' },
  'add-database': { id: 'add-database', keys: ['a', 'd'], chord: true, label: 'Add a Database', hint: 'Drop a Database node from the keyboard' },
  'add-api': { id: 'add-api', keys: ['a', 'a'], chord: true, label: 'Add an API Gateway', hint: 'Drop an API node from the keyboard' },
  'add-person': { id: 'add-person', keys: ['a', 'p'], chord: true, label: 'Add a Person', hint: 'Drop a Person node from the keyboard' },
  'add-queue': { id: 'add-queue', keys: ['a', 'q'], chord: true, label: 'Add a Queue', hint: 'Drop a Queue node from the keyboard' },
  'add-external': { id: 'add-external', keys: ['a', 'e'], chord: true, label: 'Add an External', hint: 'Drop an External node from the keyboard' },
  'add-system': { id: 'add-system', keys: ['a', 'y'], chord: true, label: 'Add a System', hint: 'Drop a System node from the keyboard' },
  'add-decision': { id: 'add-decision', keys: ['a', 'x'], chord: true, label: 'Add a Decision', hint: 'Drop a Decision node from the keyboard' },
  'add-diamond': { id: 'add-diamond', keys: ['a', 'i'], chord: true, label: 'Add a Diamond', hint: 'Drop a Diamond node from the keyboard' },
  'add-group-node': { id: 'add-group-node', keys: ['a', 'g'], chord: true, label: 'Add a Group node', hint: 'Drop an empty architecture group from the keyboard' },
  'add-box': { id: 'add-box', keys: ['a', 'b'], chord: true, label: 'Add a Box', hint: 'Drop a Box at the cursor' },
  'add-note': { id: 'add-note', keys: ['a', 'n'], chord: true, label: 'Add a Note', hint: 'Drop a sticky note from the keyboard' },
  'add-text': { id: 'add-text', keys: ['a', 't'], chord: true, label: 'Type on canvas', hint: 'Enter typed-text mode, then click the canvas' },
  'add-participant': { id: 'add-participant', keys: ['a', 'p'], chord: true, label: 'Add a Participant', hint: 'Add a sequence participant from the keyboard' },
  'group-nodes': { id: 'group-nodes', keys: ['Ctrl', 'G'], chord: false, label: 'Architecture group', hint: 'Group selected nodes into a subgraph' },
  'enter-group': { id: 'enter-group', keys: ['Tab'], chord: false, label: 'Enter / exit group', hint: 'Tab into a selected group, Tab again to leave' },
  'sidebar-palette': { id: 'sidebar-palette', keys: ['t', 'p'], chord: true, label: 'Palette tab', hint: 'Jump the sidebar to Palette' },
  'sidebar-info': { id: 'sidebar-info', keys: ['t', 'i'], chord: true, label: 'Info tab', hint: 'Jump the sidebar to Info' },
  'sidebar-pages': { id: 'sidebar-pages', keys: ['t', 'g'], chord: true, label: 'Pages tab', hint: 'Jump the sidebar to Pages' },
  'add-socket-in': { id: 'add-socket-in', keys: ['n', 'i'], chord: true, label: 'Add input socket', hint: 'Select one node, then add an input' },
  'add-socket-out': { id: 'add-socket-out', keys: ['n', 'o'], chord: true, label: 'Add output socket', hint: 'Select one node, then add an output' },
  'rotate-direction': { id: 'rotate-direction', keys: ['r', 'n'], chord: true, label: 'Rotate direction', hint: 'Cycle LTR → TTB → RTL on selected nodes' },
  'change-color': { id: 'change-color', keys: ['c', 'c', '0–9'], chord: true, label: 'Preset color', hint: 'c then c then 0–9 paints the selection. c then c then c opens the picker' },
  'add-layer': { id: 'add-layer', keys: ['l', 'n'], chord: true, label: 'New layer', hint: 'Create a layer without leaving the canvas' },
  'dim-layer': { id: 'dim-layer', keys: ['l', 'd'], chord: true, label: 'Dim active layer', hint: 'Dim or undim the active layer' },
  'hide-layer': { id: 'hide-layer', keys: ['l', 'h'], chord: true, label: 'Hide active layer', hint: 'Toggle visibility of the active layer' },
  'draw-frame': { id: 'draw-frame', keys: ['B'], chord: false, label: 'Draw a frame', hint: 'Hold B and drag — visual annotation only' },
  'draw-box': { id: 'draw-box', keys: ['d', 'b'], chord: true, label: 'Draw a box', hint: 'd then b to place one; d d then b to keep drawing. 2 also cycles the box tool' },
  'toggle-animation': { id: 'toggle-animation', keys: ['t', 'a'], chord: true, label: 'Animate edges', hint: 'Toggle flow animation on selected edges' },
  'distraction-free': { id: 'distraction-free', keys: ['t', 'f'], chord: true, label: 'Distraction-free', hint: 'Hide chrome so the canvas fills the screen' },
  'command-palette': { id: 'command-palette', keys: ['Ctrl', 'K'], chord: false, label: 'Command palette', hint: 'Jump to a page, node, catalog item, or action' },
  'ai-chat': { id: 'ai-chat', keys: ['Ctrl', 'Shift', 'A'], chord: false, label: 'AI chat', hint: 'Toggle the in-app AI panel' },
  'undo': { id: 'undo', keys: ['Ctrl', 'Z'], chord: false, label: 'Undo', hint: 'Undo the last edit' },
  'redo': { id: 'redo', keys: ['Ctrl', 'Shift', 'Z'], chord: false, label: 'Redo', hint: 'Redo the last undone edit' },
  'delete-selection': { id: 'delete-selection', keys: ['Del'], chord: false, label: 'Delete', hint: 'Remove the current selection' },
  'page-back': { id: 'page-back', keys: ['Ctrl', '['], chord: false, label: 'Previous page', hint: 'Go back in page history' },
  'page-forward': { id: 'page-forward', keys: ['Ctrl', ']'], chord: false, label: 'Next page', hint: 'Go forward in page history' },
  'rename': { id: 'rename', keys: ['Enter'], chord: false, label: 'Rename', hint: 'Rename the selected node in place' },
  'copy': { id: 'copy', keys: ['Ctrl', 'C'], chord: false, label: 'Copy', hint: 'Copy the selection' },
  'cut': { id: 'cut', keys: ['Ctrl', 'X'], chord: false, label: 'Cut', hint: 'Cut the selection' },
  'paste': { id: 'paste', keys: ['Ctrl', 'V'], chord: false, label: 'Paste', hint: 'Paste at the cursor' },
  'info-panel': { id: 'info-panel', keys: ['I'], chord: false, label: 'Open info', hint: 'Tap I to open details for what’s under the cursor' },
  'inspect': { id: 'inspect', keys: ['hold I'], chord: false, label: 'Inspect', hint: 'Hold I to overlay packed info, links, and catalog' },
  'select-all': { id: 'select-all', keys: ['Ctrl', 'A'], chord: false, label: 'Select all', hint: 'Select every item on this page' },
  'resize-drawing': {
    id: 'resize-drawing',
    keys: ['Shift', 'Alt', 'Ctrl'],
    chord: false,
    hold: true,
    label: 'Resize modifiers',
    hint: 'Hold while dragging the corner: proportion, from center, or cascade snapped parents',
  },
  'resize-shape': {
    id: 'resize-shape',
    keys: ['Shift'],
    chord: false,
    hold: true,
    label: 'Proportional resize',
    hint: 'Hold Shift while dragging the corner to keep aspect ratio',
  },
};

const NODE_TYPE_TO_SHORTCUT: Record<string, ShortcutId> = {
  service: 'add-service',
  database: 'add-database',
  api: 'add-api',
  person: 'add-person',
  queue: 'add-queue',
  external: 'add-external',
  system: 'add-system',
  decision: 'add-decision',
  diamond: 'add-diamond',
  group: 'add-group-node',
  box: 'add-box',
  note: 'add-note',
  text: 'add-text',
};

export function shortcutIdForNodeType(type: string): ShortcutId | null {
  return NODE_TYPE_TO_SHORTCUT[type] ?? null;
}

export function formatShortcutKeys(keys: string[], chord: boolean, hold?: boolean): string {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  const mapped = keys.map(key => {
    if (key === 'Ctrl') return isMac ? '⌘' : 'Ctrl';
    if (key === 'Del') return 'Delete';
    return key;
  });
  if (hold) return mapped.join(' · ');
  return chord ? mapped.join(' then ') : mapped.join('+');
}

export function shortcutDisplay(id: ShortcutId): string {
  const def = SHORTCUTS[id];
  return formatShortcutKeys(def.keys, def.chord, def.hold);
}
